import enContent from "@/content/i18n/en.json";
import msContent from "@/content/i18n/ms.json";
import zhContent from "@/content/i18n/zh.json";
import { defaultLocale, type Locale } from "@/lib/i18n";

interface MessageContent {
  messages?: Record<string, string>;
  nav?: {
    labels?: Record<string, string>;
    linkLabels?: Record<string, string>;
  };
}

const messageContent: Record<Locale, MessageContent> = {
  en: enContent,
  ms: msContent,
  zh: zhContent,
};

export function normalizeMessageKey(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

function isNonTranslatableLiteral(text: string): boolean {
  const normalizedText = normalizeMessageKey(text);

  if (!normalizedText) {
    return false;
  }

  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedText);
  const isPhoneNumber = /^\+?[\d\s().-]{7,}$/.test(normalizedText);
  const isStreetAddress =
    /\bNo\.\s*\d/i.test(normalizedText) ||
    /\bJalan\b/i.test(normalizedText) ||
    /\bSeksyen\b/i.test(normalizedText) ||
    /\b40170\b/.test(normalizedText);

  return isEmail || isPhoneNumber || isStreetAddress;
}

function getPatternTranslation(locale: Locale, text: string): string | undefined {
  const visitMatch = text.match(/^Visit (.+)$/);
  if (visitMatch) {
    return locale === "ms"
      ? `Lawati ${visitMatch[1]}`
      : `访问 ${visitMatch[1]}`;
  }

  const learnPartnershipMatch = text.match(/^Learn more about (.+) partnership$/);
  if (learnPartnershipMatch) {
    return locale === "ms"
      ? `Ketahui lanjut tentang kerjasama ${learnPartnershipMatch[1]}`
      : `了解 ${learnPartnershipMatch[1]} 合作伙伴关系`;
  }

  const learnMatch = text.match(/^Learn more about (.+)$/);
  if (learnMatch) {
    return locale === "ms"
      ? `Ketahui lanjut tentang ${learnMatch[1]}`
      : `了解更多关于 ${learnMatch[1]} 的信息`;
  }

  return undefined;
}

export function t(locale: Locale | undefined, text: string): string {
  if (!locale || locale === defaultLocale) {
    return text;
  }

  const content = messageContent[locale];
  const normalizedText = normalizeMessageKey(text);

  if (isNonTranslatableLiteral(normalizedText)) {
    return text;
  }

  const translatedText =
    content.messages?.[text] ??
    content.messages?.[normalizedText] ??
    content.nav?.labels?.[text] ??
    content.nav?.labels?.[normalizedText] ??
    content.nav?.linkLabels?.[text] ??
    content.nav?.linkLabels?.[normalizedText] ??
    getPatternTranslation(locale, normalizedText) ??
    text;

  if (locale === "zh") {
    return translatedText.split("Anchor Sprint").join("\u5b89\u514b\u6781\u901f");
  }

  return translatedText;
}
